/**
 * Weekly occupancy of a CAMPUS (F3-A): room × session grid. Data via REST
 * (GET /api/campuses/:id/occupancy?week=), not local-first (heavy aggregation).
 */
import { type Component, createMemo, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Building2, ChevronLeft, ChevronRight } from "@fleet/icons";
import { localDateKey } from "@space/shared";
import { api, createApiQuery } from "@/lib/api";
import { useI18n } from "@/providers/i18n-provider";
import { Skeleton } from "@fleet/ui";
import { ServerDataGate } from "@/components/ui/server-data-gate";

function weekMonday(d: Date): Date {
  const copy = new Date(d);
  const dow = (copy.getDay() + 6) % 7;
  copy.setDate(copy.getDate() - dow);
  copy.setHours(0, 0, 0, 0);
  return copy;
}
function ymd(d: Date): string {
  // LOCAL date: toISOString() gives the UTC date and in UTC+ timezones midnight
  // local falls on "yesterday" (COV — same family as the session generator).
  return localDateKey(d);
}

export const OccupancyView: Component<{ campusId: string }> = (props) => {
  const { t } = useI18n();
  const [anchor, setAnchor] = createSignal(new Date());
  const week = createMemo(() => ymd(weekMonday(anchor())));

  const query = createApiQuery(
    () => ["academy-occupancy", props.campusId, week()],
    async () =>
      api.GET("/api/campuses/{id}/occupancy", {
        params: { path: { id: props.campusId }, query: { week: week() } },
      }),
    () => ({ staleTime: 15_000 }),
  );

  const days = createMemo(() => {
    const start = weekMonday(anchor());
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      return ymd(d);
    });
  });

  const cell = (roomId: string, day: string) =>
    (query.data?.sessions ?? []).filter((s) => s.room_id === roomId && s.date === day);

  const shift = (weeks: number) => {
    const d = new Date(anchor());
    d.setDate(d.getDate() + weeks * 7);
    setAnchor(d);
  };

  return (
    <div class="mx-auto max-w-5xl space-y-4 p-4 md:p-6" data-testid="occupancy-view">
      <h1 class="flex items-center gap-2 font-display text-2xl font-extrabold">
        <Building2 class="h-6 w-6 text-primary" /> {t(msg`Room occupancy`)}
      </h1>
      <div class="flex items-center justify-between">
        <button
          type="button"
          data-testid="occupancy-prev"
          onClick={() => shift(-1)}
          class="rounded-lg p-2 hover:bg-muted"
          aria-label={t(msg`Previous week`)}
        >
          <ChevronLeft class="h-5 w-5" />
        </button>
        <span class="text-sm font-semibold">{week()}</span>
        <button
          type="button"
          data-testid="occupancy-next"
          onClick={() => shift(1)}
          class="rounded-lg p-2 hover:bg-muted"
          aria-label={t(msg`Next week`)}
        >
          <ChevronRight class="h-5 w-5" />
        </button>
      </div>

      {/* LF-B: occupancy is server-only (aggregation) — offline shows an
          honest state instead of an eternal skeleton. */}
      <ServerDataGate
        pending={query.isLoading}
        error={query.isError}
        refetch={() => void query.refetch()}
        skeleton={<Skeleton class="h-64 w-full rounded-2xl" />}
      >
        <Show
          when={(query.data?.rooms ?? []).length > 0}
          fallback={<p class="text-sm text-muted-foreground">{t(msg`No rooms in this campus.`)}</p>}
        >
          <div class="overflow-x-auto">
            <table class="w-full border-collapse text-xs" data-testid="occupancy-grid">
              <thead>
                <tr>
                  <th class="border border-border p-2 text-left">{t(msg`Room`)}</th>
                  <For each={days()}>
                    {(d) => <th class="border border-border p-2 font-semibold">{d.slice(5)}</th>}
                  </For>
                </tr>
              </thead>
              <tbody>
                <For each={query.data?.rooms ?? []}>
                  {(room) => (
                    <tr data-testid="occupancy-room-row">
                      <td class="border border-border p-2 font-semibold">{room.name}</td>
                      <For each={days()}>
                        {(day) => (
                          <td class="border border-border p-1 align-top">
                            <For each={cell(room.id, day)}>
                              {(s) => (
                                <div class="mb-1 rounded bg-accent/12 px-1 py-0.5 text-accent">
                                  {s.start_time}–{s.end_time}
                                </div>
                              )}
                            </For>
                          </td>
                        )}
                      </For>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>
        </Show>
      </ServerDataGate>
    </div>
  );
};
